import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { businessWorkLocationCheckQuery, YesNoBlank } from '@/__generated__/businessWorkLocationCheckQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { AnswerList, CTA, ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertWorkingAtRegisteredAddress } from '@/graphql/mutation/survey/business';
import { workLocationCheckQuery } from '@/graphql/query/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';

const answers: { value: YesNoBlank; label: string }[] = [
  { value: 'yes', label: '네, 사업장에서 일했어요' },
  { value: 'no', label: '아니요, 사업장 외부에서 일했어요' }
];

export const WorkLocationCheckContainer = ({ queryRef }: { queryRef: PreloadedQuery<businessWorkLocationCheckQuery> }) => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();

  const { getSurveyBusinessAnswer } = usePreloadedQuery<businessWorkLocationCheckQuery>(workLocationCheckQuery, queryRef);
  const [upsertBusinessAnswer, isLoading] = useUpsertBusinessAnswer(upsertWorkingAtRegisteredAddress);

  const [selectedAnswer, setSelectedAnswer] = useState<YesNoBlank>('blank');

  useEffect(() => {
    if (getSurveyBusinessAnswer.result && getSurveyBusinessAnswer.businessAnswer?.[0]?.isWorkingAtRegisteredAddress) {
      setSelectedAnswer(getSurveyBusinessAnswer.businessAnswer?.[0]?.isWorkingAtRegisteredAddress || 'blank');
    }
  }, [getSurveyBusinessAnswer.businessAnswer, getSurveyBusinessAnswer.result]);

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_work-location-check_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_work-location-check_confirm', eventAttributes);

            upsertBusinessAnswer(
              { isWorkingAtRegisteredAddress: selectedAnswer },
              {
                onCompleted: () => {
                  router.push(SURVEY_PAGES.사업장_자가구매);
                }
              }
            );
          }}
        >
          <article>
            <ProgressBar step={3} />

            <section>
              <h1 className={common.content_title}>
                등록한 사업장 주소지로
                <br />
                실제 출근하셨나요?
              </h1>

              <AnswerList
                answers={answers}
                checkedValue={selectedAnswer}
                onChange={(value) => {
                  sendClickEvent(`survey_work-location-check_${value}`, eventAttributes);

                  setSelectedAnswer(value);
                }}
              />
            </section>

            <section className={'pt-6'}>
              <h2 className={common.section_title}>등록 사업장 근무 여부, 왜 확인이 필요한가요?</h2>
              <p className={common.section_list_item}>감면 및 공제를 정확하게 적용하려면 실제로 사업을 하는 곳인지 확인이 필요합니다.</p>
            </section>
          </article>

          {selectedAnswer !== 'blank' && (
            <CTA type={'submit'} disabled={isLoading}>
              다음
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};
